import React, { memo, useState, useEffect, useRef } from 'react'
import { render } from 'react-dom'
import Flatpickr from 'react-flatpickr'
import classNames from 'classnames'
import { Calendar } from 'components/atoms/icons'
import moment from 'moment'
import PropTypes from 'prop-types'
import uuid from 'uuid/v4'
import { FORMATS } from 'constants/datetime'
import { FLATPICKR_DEFAULTS } from 'constants/formElements'
import 'flatpickr/dist/themes/airbnb.css'
import 'scss/5_components/_new-date-picker.scss'

const EMPTY_ARRAY = []

const ClearButton = memo(({ onClick }) => (
  <button type='button' className='btn-tertiary calendar-clear-button' onClick={onClick}>
    Clear
  </button>
))

// This is a formatting string specific to Flatpickr - see
// https://flatpickr.js.org/formatting/#date-formatting-tokens
const FLATPICKR_DATE_FORMAT = 'm/d/Y'

export const DatePicker = memo(
  ({
    ariaLabel,
    children,
    dataTest,
    date,
    disabled,
    id,
    invalid,
    large,
    maxDate,
    minDate,
    onBlur,
    onDateChange,
    placeholder,
    readonly,
    required,
    validationMessage,
    wrapValidationText = true,
  }) => {
    const datePickerOptions = {}

    if (maxDate) {
      datePickerOptions.maxDate = maxDate
    }
    if (minDate) {
      datePickerOptions.minDate = minDate
    }
    // Flatpickr uses different formatting tokens from the ones moment uses.
    // So we're hardcoding the formatting string here for Flatpickr
    datePickerOptions.dateFormat = FLATPICKR_DATE_FORMAT

    const [open, setOpen] = useState(false)
    const [flatpickr, setFlatpickr] = useState()
    const containerRef = useRef(null)

    const openCalendarHandler = () => setOpen(true)

    const changeDateHandler = dates => onDateChange(dates, id)

    const clearDateHandler = () => onDateChange(EMPTY_ARRAY, id)

    const closeCalendarHandler = (oldDates, newDate, config) => {
      const button = document.getElementsByClassName('calendar-clear-button')[0]
      if (button) button.parentNode.removeChild(button)
      setOpen(false)
    }

    const toggleCalendarHandler = () => {
      if (!flatpickr) return
      if (open) {
        flatpickr.close()
      } else {
        flatpickr.open()
      }
    }

    const validationText = validationMessage || (required ? 'Required' : '')

    useEffect(() => {
      if (open && !readonly) {
        const flatpickrContainer = document.getElementsByClassName('flatpickr-calendar open')[0]
        const container = document.createElement('div')
        if (flatpickrContainer) flatpickrContainer.appendChild(container)
        render(<ClearButton onClick={clearDateHandler} />, container)
      }
    }, [open])

    const onReady = (dObj, dStr, fp) => setFlatpickr(fp)

    const ignoredFocusElements =
      readonly && containerRef.current ? Array.from(containerRef.current.children) : EMPTY_ARRAY

    const classes = classNames('date-picker-container', {
      invalid,
      disabled,
      'date-picker-large': large,
      'date-picker-readonly': readonly,
      'date-picker-open': open,
      'date-picker-nowrap': !wrapValidationText,
    })

    // singleDateFormat is the `moment.js` format that matches the flatpickr
    // formatting string that we've chosen to use.
    const defaultDate = date ? moment.parseZone(date).format(FORMATS.singleDateFormat) : undefined
    return (
      <div data-test={`${dataTest}-container`} className={classes} ref={containerRef}>
        {readonly ? (
          React.cloneElement(children, {
            onClick: toggleCalendarHandler,
            className: [children.props.className, 'date-picker-display-value'].join(' '),
          })
        ) : (
          <Calendar className='date-picker-icon' />
        )}
        <Flatpickr
          data-test={dataTest}
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          className='date-picker'
          aria-label={ariaLabel}
          options={{
            ...FLATPICKR_DEFAULTS,
            ...datePickerOptions,
            altInput: { 'data-test': dataTest, required },
            ...(readonly
              ? {
                  allowInput: false,
                  ignoredFocusElements,
                }
              : {}),
          }}
          value={defaultDate}
          onOpen={openCalendarHandler}
          onClose={closeCalendarHandler}
          onChange={changeDateHandler}
          onReady={onReady}
          onBlur={onBlur}
        />
        {validationText && <span className='validation-message'>{validationText}</span>}
      </div>
    )
  }
)

DatePicker.propTypes = {
  date: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onDateChange: PropTypes.func,
  id: PropTypes.string,
  minDate: PropTypes.instanceOf(Calendar),
  maxDate: PropTypes.instanceOf(Calendar),
  invalid: PropTypes.bool,
  required: PropTypes.bool,
  validationMessage: PropTypes.string,
  dataTest: PropTypes.string,
  large: PropTypes.bool,
}

DatePicker.defaultProps = {
  date: '',
  placeholder: 'Start Date',
  disabled: false,
  onDateChange: () => {},
  id: uuid(),
  minDate: undefined,
  maxDate: undefined,
  invalid: false,
  required: false,
  validationMessage: '',
  dataTest: 'single-date-picker',
  large: false,
}

ClearButton.propTypes = {
  onClick: PropTypes.func.isRequired,
}
